/**
 * `theme/assets/ugc-gallery.js` — the Worn by You lightbox.
 *
 * Every post's lightbox content is already in the page as a `<template>`
 * (server-rendered by sections/ugc-gallery.liquid, in card order), so opening
 * a post is a clone, not a fetch. A native `<dialog>` with `showModal()` gives
 * the focus trap, Esc, and the top layer — the last one matters because the
 * fixed corner stack (WhatsApp, chat, Rewards, 15% card) sits on high z-index
 * values that no in-page z-index would reliably beat.
 *
 * The decisions (which post next, was that a swipe, which arrows show) live
 * in ugc-gallery-nav.js, where `node --test` can reach them.
 */
import { stepIndex, swipeStep, arrowState, backdropClose, focusAfterStep } from '@theme/ugc-gallery-nav';

class UgcGallery extends HTMLElement {
  #index = 0;
  /** @type {HTMLElement | null} */
  #opener = null;
  /** @type {{ x: number, y: number } | null} */
  #start = null;
  #pressOnDialog = false;
  /** @type {AbortController | null} */
  #listeners = null;

  connectedCallback() {
    this.dialog = this.querySelector('[data-ugc-dialog]');
    if (!this.dialog) return; // Theme-editor placeholders only.

    this.body = this.dialog.querySelector('[data-ugc-dialog-body]');
    this.templates = [...this.querySelectorAll('template[data-ugc-post]')];
    this.prev = this.dialog.querySelector('[data-ugc-step="-1"]');
    this.next = this.dialog.querySelector('[data-ugc-step="1"]');

    // One signal for every listener: the Theme editor re-renders sections,
    // and a reconnect must not leave the old dialog's handlers behind.
    this.#listeners = new AbortController();
    const signal = this.#listeners.signal;
    this.addEventListener('click', this.#onClick, { signal });
    this.dialog.addEventListener('keydown', this.#onKey, { signal });
    this.dialog.addEventListener('close', this.#onClose, { signal });
    this.dialog.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.dialog.addEventListener('pointerup', this.#onPointerUp, { signal });
    this.dialog.addEventListener('pointercancel', this.#onPointerCancel, { signal });
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#listeners = null;
  }

  /** @param {number} index */
  #show(index) {
    const count = this.templates.length;
    this.#index = stepIndex(index, 0, count);
    this.body.replaceChildren(this.templates[this.#index].content.cloneNode(true));
    const state = arrowState(this.#index, count);
    const focused = document.activeElement === this.next ? 'next' : document.activeElement === this.prev ? 'prev' : null;
    this.prev.disabled = !state.prev;
    this.next.disabled = !state.next;
    const target = focusAfterStep(focused, state);
    if (target) ({ prev: this.prev, next: this.next, dialog: this.dialog })[target].focus();
  }

  /** @param {number} delta */
  #step(delta) {
    this.#show(stepIndex(this.#index, delta, this.templates.length));
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {Element} */ (event.target);

    // A click on the dialog element itself, not its content, is the backdrop --
    // but only when the press began there too, not a drag out of the panel.
    if (target === this.dialog) {
      if (backdropClose(this.#pressOnDialog, true)) this.dialog.close();
      return;
    }

    const card = target.closest('[data-ugc-index]');
    if (card && !this.dialog.contains(card)) {
      this.#opener = /** @type {HTMLElement} */ (card);
      this.#show(Number(card.getAttribute('data-ugc-index')));
      this.dialog.showModal();
      // showModal() focuses the first link ("View on Instagram") and paints a
      // focus ring on a tap. Start on the dialog itself; Tab still enters it.
      this.dialog.focus();
      return;
    }

    const step = target.closest('[data-ugc-step]');
    if (step) {
      this.#step(Number(step.getAttribute('data-ugc-step')));
      return;
    }

    if (target.closest('[data-ugc-close]')) this.dialog.close();
  };

  /** @param {KeyboardEvent} event */
  #onKey = (event) => {
    if (event.key === 'ArrowRight') this.#step(1);
    if (event.key === 'ArrowLeft') this.#step(-1);
  };

  #onClose = () => {
    this.body.replaceChildren();
    this.#opener?.focus();
    this.#opener = null;
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    this.#pressOnDialog = event.target === this.dialog;
    this.#start = event.pointerType === 'touch' ? { x: event.clientX, y: event.clientY } : null;
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    if (!this.#start) return;
    const delta = swipeStep(event.clientX - this.#start.x, event.clientY - this.#start.y);
    this.#start = null;
    if (delta) this.#step(delta);
  };

  /** The browser took the gesture (a vertical scroll of the panel). */
  #onPointerCancel = () => {
    this.#start = null;
  };
}

if (!customElements.get('ugc-gallery')) customElements.define('ugc-gallery', UgcGallery);
